// /appv3/animation — V3 Animation Studio (Jun 14).
//
// Two-step Studio layout mirroring Product Shot V3's pattern. This file
// is the AnimationStudio component itself; it's reached via the
// /appv3/:category route in router/config.tsx, which early-returns
// into this component from /appv3/page.tsx when category==='animation'.
// The standalone /animation URL is preserved as a redirect for
// back-compat with /templates deep-links.
//
//   Step 1: Pick template + select images
//   Step 2: Add text + generate MP4
//
// Animation is its own beast — no CreativeDirection, no backend
// /api/wizard/ai-generate; client-side WebCodecs encoding via
// lib/animation/{render,encode}. So we early-return into THIS rather
// than going through FashionStudioLayout.
//
// Source images: My Creatives only (the AI-generate-4-from-products
// path was dropped Jun 14 per user direction — keep V3 simple).
//
// Pricing: 1 credit per export session via /api/animation/charge.
//
// Aspect ratios (Jun 14 user lock): templates are NOT locked to a
// single aspect. The aspect picker is wired and the renderer respects
// the chosen canvas dims; some motion compositions (mosaic-scroll's
// 3-column layout, etc.) are still tuned for 4:5 and will need
// per-motion tuning at non-4:5 aspects — that's Round 2 work.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  InsufficientCreditsError,
  chargeAnimationExport,
} from '@/lib/api';
import {
  getSignedImageUrl,
  listPosts,
  savePost,
  type Post,
} from '@/lib/gallery';
import { useCredits } from '@/hooks/useCredits';
import { useBrand } from '@/hooks/useBrand';
import {
  ANIM_DIMENSIONS,
  ANIM_FONTS,
  TEXT_STYLES,
  loadAnimImage,
  specDurationMs,
  type AnimAspect,
  type AnimFont,
  type AnimationSpec,
  type CaptionScrim,
  type MotionStyle,
  type TextStyle,
} from '@/lib/animation/types';
import { drawAnimationFrame } from '@/lib/animation/render';
import {
  ANIMATION_TEMPLATES,
  getAnimationTemplate,
  type AnimationTemplate,
} from '@/mocks/animation-templates';
import {
  encodeAnimationToMp4,
  isAnimationExportSupported,
} from '@/lib/animation/encode';

// ============================================================================
// Constants
// ============================================================================

/** 3×3 placement grid stops — vertical × horizontal. Mirrors StudioFinishEditor's
 *  placement control on /appv3 Product Shot. */
const CAPTION_Y_STOPS = [0.2, 0.5, 0.8] as const;
const CAPTION_ALIGNS = ['left', 'center', 'right'] as const;

/** Backdrop-scrim variants behind captions (Jul 24). Each swatch previews
 *  the gradient direction so the picker reads at a glance. */
const SCRIM_OPTS: { id: CaptionScrim; name: string; grad: string }[] = [
  { id: 'band', name: 'Soft', grad: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.7) 50%, rgba(0,0,0,0))' },
  { id: 'bottom', name: 'From bottom', grad: 'linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0))' },
  { id: 'top', name: 'From top', grad: 'linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0))' },
  { id: 'none', name: 'None', grad: '' },
];

const MAX_IMAGES = 6;
const DEFAULT_PER_IMAGE_SEC = 1.5;

/** Aspect picker options. Templates ship at 4:5 by default but the user
 *  can override — the renderer reads canvas.width / canvas.height so the
 *  motion math compiles at any aspect (composition tuning per motion is
 *  Round 2 work). */
const ASPECT_OPTIONS: ReadonlyArray<{ id: AnimAspect; label: string }> = [
  { id: '1:1', label: '1:1' },
  { id: '4:5', label: '4:5' },
  { id: '9:16', label: '9:16' },
  // 16:9 isn't currently in AnimAspect — see lib/animation/types.ts. If
  // we add it there, surface it here too.
];

// Scale every timing field (any `*Ms` number) of a template's motionConfig by
// `factor`. The "Per image" slider only drove the basic sequence motions (which
// read spec.perImageMs); the TEMPLATE motions (mosaic/card-swipe/angled-cut/
// collage/…) run on their own baked scrollMs/holdMs cycle and ignored it — so
// the slider changed the loop LENGTH but never the on-screen PACE. Scaling the
// cycle timing makes "Per image" a real speed control for every motion.
function scaleMotionTiming<T extends object>(cfg: T, factor: number): T {
  if (!Number.isFinite(factor) || factor === 1) return cfg;
  const out = { ...cfg } as Record<string, unknown>;
  for (const k of Object.keys(out)) {
    if (k.endsWith('Ms') && typeof out[k] === 'number') {
      out[k] = Math.max(1, Math.round((out[k] as number) * factor));
    }
  }
  return out as T;
}

// ============================================================================
// Main component
// ============================================================================

export default function AnimationStudio() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { balance, refresh: refreshCredits } = useCredits();
  const { activeBrand } = useBrand();

  // ---------- Two-step flow ----------
  // Step 1: pick template + images. Step 2: edit text + generate.
  const [step, setStep] = useState<1 | 2>(1);

  // Animation studio is desktop-only — the multi-image timeline + motion/text
  // controls + video export can't lay out on a phone/tablet. isNarrow gates the
  // whole page to a "desktop only" notice below lg (1024px).
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---------- Active template ----------
  // Default-pick the first template on mount so Step 1 always has a
  // preview to show. Deep-link via ?template=<id> overrides this on
  // subsequent renders (effect below).
  const [activeTemplate, setActiveTemplate] = useState<AnimationTemplate>(
    () => ANIMATION_TEMPLATES[0],
  );

  // ---------- Core selections ----------
  const [aspect, setAspect] = useState<AnimAspect>(
    () => ANIMATION_TEMPLATES[0].aspectRatio,
  );
  const [motionStyle, setMotionStyle] = useState<MotionStyle>(
    () => ANIMATION_TEMPLATES[0].motionStyle,
  );
  const [textStyle, setTextStyle] = useState<TextStyle>(
    () => ANIMATION_TEMPLATES[0].captionStyle ?? 'word-pop',
  );
  const [perImageSec, setPerImageSec] = useState(DEFAULT_PER_IMAGE_SEC);

  // ---------- Global captions (Jun 15 — was per-image, now cycling list) ----------
  // List of caption strings, independent of images. Each non-empty caption
  // gets an equal share of the total animation duration; they cycle in
  // order. Empty rows are kept in state (so the user can have a half-typed
  // row) but filtered out before spec assembly so they don't take up a
  // time slot in the rendered video.
  const [captionsList, setCaptionsList] = useState<string[]>([]);
  const addCaption = useCallback(() => {
    setCaptionsList((prev) => [...prev, '']);
  }, []);
  const updateCaption = useCallback((idx: number, value: string) => {
    setCaptionsList((prev) => prev.map((c, i) => (i === idx ? value : c)));
  }, []);
  const removeCaption = useCallback((idx: number) => {
    setCaptionsList((prev) => prev.filter((_, i) => i !== idx));
  }, []);
  const [captionFont, setCaptionFont] = useState<AnimFont>(() => {
    const tplFont = ANIMATION_TEMPLATES[0].captionFont;
    return tplFont
      ? ANIM_FONTS.find((x) => x.family === tplFont) ?? ANIM_FONTS[0]
      : ANIM_FONTS[0];
  });
  const [captionScale, setCaptionScale] = useState(1);
  const [captionAlign, setCaptionAlign] = useState<'left' | 'center' | 'right'>('center');
  const [captionY, setCaptionY] = useState(0.8);
  // Color controls (Jun 15) — caption text color + optional background
  // fill. Bg null = no fill (text on the soft gradient scrim only).
  const [captionTextColor, setCaptionTextColor] = useState<string>('#FFFFFF');
  const [captionBgColor, setCaptionBgColor] = useState<string | null>(null);
  // Backdrop scrim behind the caption block (Jul 24). Selectable so the
  // legibility darkening can come from below / above / a centered soft
  // band, or off. Defaults to 'band' (the original always-on behavior).
  const [captionScrim, setCaptionScrim] = useState<CaptionScrim>('band');

  // (Selected-layer state removed Jun 15 — captions are now global, not
  // per-image, so there's no need to track "which image's caption is
  // being edited".)

  // ---------- Deep-link from /templates → pre-load an animation template ----------
  useEffect(() => {
    const id = searchParams.get('template');
    if (!id) return;
    const t = getAnimationTemplate(id);
    if (!t) return;
    setActiveTemplate(t);
    setAspect(t.aspectRatio);
    setMotionStyle(t.motionStyle);
    if (t.perImageMs) setPerImageSec(t.perImageMs / 1000);
    if (t.captionStyle) setTextStyle(t.captionStyle);
    if (t.captionFont) {
      const f = ANIM_FONTS.find((x) => x.family === t.captionFont);
      if (f) setCaptionFont(f);
    }
  }, [searchParams]);

  const handlePickTemplate = useCallback((t: AnimationTemplate) => {
    setActiveTemplate(t);
    setAspect(t.aspectRatio);
    setMotionStyle(t.motionStyle);
    if (t.perImageMs) setPerImageSec(t.perImageMs / 1000);
    if (t.captionStyle) setTextStyle(t.captionStyle);
    if (t.captionFont) {
      const f = ANIM_FONTS.find((x) => x.family === t.captionFont);
      if (f) setCaptionFont(f);
    }
    // Clear the ?template= deep-link param when the user picks manually
    // so the URL reflects the current selection (idempotent — no-op if
    // already absent).
    setSearchParams((p) => {
      if (p.has('template')) {
        p.delete('template');
        return p;
      }
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // ---------- Sources: My Creatives ----------
  const [posts, setPosts] = useState<Post[]>([]);
  const [postUrls, setPostUrls] = useState<Record<string, string>>({});
  const [postsLoading, setPostsLoading] = useState(true);
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPostsLoading(true);
      try {
        const list = await listPosts(activeBrand?.id);
        // Only rendered IMAGE posts qualify as sources (skip locked
        // stubs, carousel zips, and saved animations).
        const usable = list.filter(
          (p) =>
            !!p.image_path
            && !p.image_path.endsWith('.zip')
            && !p.image_path.endsWith('.mp4'),
        );
        const urls: Record<string, string> = {};
        for (const p of usable) {
          urls[p.id] = await getSignedImageUrl(p.image_path);
        }
        if (!cancelled) {
          setPosts(usable);
          setPostUrls(urls);
        }
      } catch (err) {
        console.error('[Animation] failed to load creatives:', err);
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_IMAGES
          ? prev
          : [...prev, id],
    );
  }, []);

  // ---------- Effective image sources ----------
  const imageUrls = useMemo(
    () => selectedPostIds.map((id) => postUrls[id]).filter(Boolean),
    [selectedPostIds, postUrls],
  );
  const imagesKey = imageUrls.join('|');

  // ---------- Load images (async) ----------
  // Loading is SPLIT from spec assembly so caption keystrokes rebuild
  // the spec without reloading images — the preview keeps playing
  // (time lives in a ref inside AnimPreview).
  // `loaded` captures whatever image set is currently rendered, INCLUDING
  // the URLs (Jun 15 v3 fix) so the spec stays derivable from a stable
  // snapshot during the load window. Without urls in loaded, the spec
  // useMemo had to gate on `loaded.key !== imagesKey` and return null
  // mid-load → preview blanked → user saw "jitter and break" on every
  // image add/remove regardless of motion style.
  const [loaded, setLoaded] = useState<{
    key: string;
    urls: string[];
    images: HTMLImageElement[];
  } | null>(null);
  const [specLoading, setSpecLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (imageUrls.length === 0) {
        setLoaded(null);
        return;
      }
      setSpecLoading(true);
      try {
        // Snapshot urls + their loaded images TOGETHER so consumers
        // (spec useMemo below) always read a consistent pair. If
        // imageUrls changes mid-load, the cancelled flag drops the
        // stale result before it can replace loaded.
        const urls = imageUrls;
        const images = await Promise.all(urls.map(loadAnimImage));
        if (!cancelled) setLoaded({ key: imagesKey, urls, images });
      } catch (err) {
        console.error('[Animation] image load failed:', err);
        if (!cancelled) setLoaded(null);
      } finally {
        if (!cancelled) setSpecLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey]);

  // ---------- Spec assembly ----------
  // Derived from `loaded` (not `imageUrls`) so the spec stays stable
  // through the load window. Captions are looked up via `loaded.urls`
  // (the snapshot that matches loaded.images) — mismatch with the
  // user's latest captionsList edits is OK because captions are keyed
  // by url, not by index. When the user adds an image:
  //   1. imageUrls changes → image-load effect fires (spec untouched)
  //   2. spec still derives from OLD loaded → preview keeps playing
  //   3. image-load completes → setLoaded updates atomically → spec
  //      rebuilds with NEW images → preview swaps in one frame
  // Eliminates the spec=null window that used to blank the canvas on
  // every image add/remove regardless of motion style.
  const spec = useMemo<AnimationSpec | null>(() => {
    if (!loaded || loaded.images.length === 0) {
      return null;
    }
    // Global cycling captions (Jun 15 — was per-image). Filter to non-empty
    // strings so half-typed rows don't take up a time slot. The renderer
    // divides total animation duration evenly across these entries.
    const effectiveCaptions = captionsList
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    return {
      images: loaded.images,
      motionStyle,
      // Template tunables (mosaic-scroll columns/timing/etc.). Only
      // attached when the template's motion style matches the current
      // selection — switching motion drops the config so the new style
      // uses its own defaults. Timing is scaled by the Per-image slider vs
      // the template's natural per-image (factor 1 at load) so dragging the
      // slider actually changes the on-screen pace, not just the loop length.
      motionConfig:
        activeTemplate && activeTemplate.motionStyle === motionStyle && activeTemplate.motionConfig
          ? scaleMotionTiming(
              activeTemplate.motionConfig,
              (perImageSec * 1000) / (activeTemplate.perImageMs ?? DEFAULT_PER_IMAGE_SEC * 1000),
            )
          : undefined,
      backgroundColor:
        activeTemplate && activeTemplate.motionStyle === motionStyle
          ? activeTemplate.backgroundColor
          : undefined,
      perImageMs: perImageSec * 1000,
      transitionMs: Math.min(700, perImageSec * 1000 * 0.5),
      captions: effectiveCaptions.length > 0 ? effectiveCaptions : undefined,
      captionStyle: textStyle,
      captionFont: captionFont.family,
      captionWeight: captionFont.weight,
      captionScale,
      captionAlign,
      captionY,
      captionTextColor,
      captionBgColor,
      captionScrim,
    };
  }, [
    loaded, captionsList,
    motionStyle, perImageSec, textStyle,
    captionFont, captionScale, captionAlign, captionY,
    captionTextColor, captionBgColor, captionScrim,
    activeTemplate,
  ]);

  // ---------- Export ----------
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [savedPostId, setSavedPostId] = useState<string | null>(null);
  const chargedRef = useRef(false);
  const exportSupported = useMemo(() => isAnimationExportSupported(), []);

  // Jun 22 — single Generate CTA: encode → save to My Creatives → download
  // MP4 to disk. Previously two separate buttons (Save / Download) each
  // doing only half the action, which surprised users who hit the primary
  // "Generate Animation" CTA and got no library row. One credit per session
  // (chargedRef), so regenerating doesn't double-charge.
  const runExport = useCallback(async () => {
    if (!spec || exporting) return;
    setExporting(true);
    setExportProgress(0);
    setError(null);
    try {
      const { w, h } = ANIM_DIMENSIONS[aspect];
      const blob = await encodeAnimationToMp4({
        spec,
        width: w,
        height: h,
        onProgress: setExportProgress,
      });
      // Charge AFTER a successful encode (failure never charges), once
      // per session.
      if (!chargedRef.current) {
        await chargeAnimationExport();
        chargedRef.current = true;
        refreshCredits();
      }
      // 1. Save to My Creatives so the user has a library row regardless
      //    of where the browser download lands. Same row shape as any
      //    other gallery post — video posts render via <video> in the
      //    gallery tile.
      const id = await savePost({
        blob,
        templateId: `animation:${motionStyle}`,
        aspectRatio: aspect,
        title: `Animation · ${motionStyle}`,
        headline: null,
        contentType: 'video/mp4',
        fileExt: 'mp4',
      });
      setSavedPostId(id);
      // 2. Download the MP4 to disk so the user gets the file
      //    immediately (TikTok upload / Reels / Stories workflow).
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `animation-${Date.now()}.mp4`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      setError(
        err instanceof InsufficientCreditsError
          ? 'Not enough credits — exporting costs 1 credit. Upgrade or top up to continue.'
          : err instanceof Error
            ? err.message
            : 'Export failed.',
      );
    } finally {
      setExporting(false);
    }
  }, [spec, exporting, aspect, refreshCredits, motionStyle]);

  // ---------- Derived UI state ----------
  const canContinue = imageUrls.length > 0;
  const canExport = !!spec && !exporting && exportSupported;
  const exportFree = chargedRef.current;

  // Desktop-only gate — below lg the studio layout is unusable. Placed after
  // all hooks (before the first conditional return) so rules-of-hooks holds.
  if (isNarrow) {
    return (
      <AppLayoutV2>
        <div className="min-h-[calc(100dvh-4rem)] flex flex-col items-center justify-center px-8 py-12 text-center">
          <i className="ri-computer-line text-5xl text-primary/40 mb-4" />
          <h1 className="text-lg font-bold text-dark mb-1.5">Animation is a desktop feature</h1>
          <p className="text-sm text-dark-muted max-w-xs leading-relaxed mb-5">
            The animation studio — multi-image timeline, motion &amp; text
            controls, and video export — needs a wider screen. Open this on a
            laptop or desktop to create animations.
          </p>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
          >
            <i className="ri-arrow-left-line" />
            Back to create
          </button>
        </div>
      </AppLayoutV2>
    );
  }

  // ============================================================================
  // STEP 1 — Pick template + select images
  // ============================================================================

  if (step === 1) {
    return (
      <AppLayoutV2>
        {/* HEADER — full-width white bar matching the /appv3 Studio (Model Shot)
            header: gradient icon tile + title + subtitle, edge-to-edge with a
            border + soft shadow. */}
        <div className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 shadow-[0_2px_14px_-6px_rgba(15,15,30,0.18)]">
          <div className="w-full max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center shadow-md shadow-primary/25">
              <i className="ri-film-line text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-dark truncate leading-tight">Animation</h1>
              <p className="hidden sm:block text-xs text-dark-muted truncate">
                Pick a template + your creatives, then add text and export an MP4 for TikTok, Reels, or Stories.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-[589px_minmax(0,843px)] gap-5 items-stretch xl:h-[calc(100vh-128px)] xl:min-h-[520px] xl:max-h-[860px]">

            {/* ============ LEFT PANEL — Templates + aspect + preview ============ */}
            <div className="bg-white rounded-2xl border border-surface-dark/30 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden flex flex-col">

              {/* Templates header (Jun 15 v3 — Create tab dropped per
                  user direction "Animation is templates-only"). Was a
                  2-col tab toggle with Create disabled; now a plain
                  section header since there's no alternative path. */}
              <div className="px-5 pt-5 pb-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-dark flex items-center gap-2">
                  <i className="ri-layout-grid-line text-base text-primary" />
                  Templates
                </p>
              </div>

              {/* Templates horizontal-scroll row */}
              <div className="px-5 pb-3">
                <TemplatesRow
                  templates={ANIMATION_TEMPLATES}
                  activeId={activeTemplate.id}
                  onPick={handlePickTemplate}
                />
              </div>

              {/* Aspect ratio pills */}
              <div className="px-5 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-dark-muted">
                    Aspect ratio
                  </span>
                  <div className="flex p-0.5 rounded-full bg-surface border border-surface-dark/30">
                    {ASPECT_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setAspect(opt.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${
                          aspect === opt.id ? 'bg-white text-primary shadow' : 'text-dark-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview area — shows the LIVE canvas-rendered animation
                  with the user's selected images when at least one image
                  is selected (spec ≠ null). Before any selection, falls
                  back to the template's baked sample MP4 so users still
                  see what the motion looks like at first glance.
                  Switched Jun 15 v3 per user direction "show preview
                  with selected product images — easier to see what you
                  get". Same AnimPreview component as Step 2; spec
                  identity persists across image picks so the playhead
                  doesn't reset on each addition. */}
              <div className="flex-1 min-h-0 border-t border-surface-dark/20 bg-surface-dark/30 flex items-center justify-center p-4 overflow-hidden">
                {spec ? (
                  <AnimPreview spec={spec} aspect={aspect} loading={specLoading} />
                ) : (
                  <TemplatePreviewVideo
                    template={activeTemplate}
                    aspect={aspect}
                  />
                )}
              </div>
            </div>

            {/* ============ RIGHT PANEL — Select images + Continue ============ */}
            <div className="bg-white rounded-2xl border border-surface-dark/30 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden flex flex-col">

              {/* Header */}
              <div className="px-5 pt-5 pb-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-dark-muted">
                  Select images
                </p>
                <p className="text-sm font-bold text-dark mt-1">From My Creatives</p>
                <p className="text-xs text-dark-muted mt-1">
                  Pick 1–{MAX_IMAGES} images. The number is the play order.
                </p>
              </div>

              {/* Image grid */}
              <div className="flex-1 min-h-0 px-5 pb-3 overflow-y-auto">
                {postsLoading ? (
                  <p className="text-sm text-dark-muted py-4 flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Loading your creatives…
                  </p>
                ) : posts.length === 0 ? (
                  <p className="text-sm text-dark-muted py-4">
                    Nothing in My Creatives yet — generate some creatives first to use them here.
                  </p>
                ) : (
                  <div className="grid grid-cols-5 gap-2">
                    {posts.map((p) => {
                      const url = postUrls[p.id];
                      const orderIdx = selectedPostIds.indexOf(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePostSelected(p.id)}
                          title={p.title ?? 'Creative'}
                          className={`
                            relative aspect-square rounded-lg overflow-hidden bg-surface
                            border-2 transition-all cursor-pointer
                            ${orderIdx >= 0
                              ? 'border-primary ring-2 ring-primary/30'
                              : 'border-transparent hover:border-primary/40'}
                          `}
                        >
                          {url && (
                            <img
                              src={url}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover"
                              loading="lazy"
                            />
                          )}
                          {orderIdx >= 0 && (
                            <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shadow">
                              {orderIdx + 1}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer — Continue */}
              <div className="px-5 py-4 border-t border-surface-dark/20 flex items-center justify-between gap-3">
                <p className="text-xs text-dark-muted">
                  {imageUrls.length === 0
                    ? 'Pick at least 1 image to continue.'
                    : `${imageUrls.length} of ${MAX_IMAGES} selected`}
                </p>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!canContinue}
                  className={`
                    inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer
                    ${canContinue
                      ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                      : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                  `}
                >
                  CONTINUE
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </AppLayoutV2>
    );
  }

  // ============================================================================
  // STEP 2 — Add text + generate MP4
  // ============================================================================

  // Has-any-caption check now reads from the global list (Jun 15).
  const hasAnyCaption = captionsList.some((c) => c.trim().length > 0);

  return (
    <AppLayoutV2>
      {/* HEADER — full-width white bar matching the /appv3 Studio (Model Shot)
          header: back button + gradient icon tile + title + subtitle. */}
      <div className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 shadow-[0_2px_14px_-6px_rgba(15,15,30,0.18)]">
        <div className="w-full max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setStep(1)}
            aria-label="Back to images"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-arrow-left-line text-lg" />
            <span className="hidden sm:inline text-sm font-medium">Back</span>
          </button>
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center shadow-md shadow-primary/25">
            <i className="ri-film-line text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-dark truncate leading-tight">Add text &amp; generate</h1>
            <p className="hidden sm:block text-xs text-dark-muted truncate">
              Add captions that cycle through the animation. Pick the style, then export your MP4.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-[589px_minmax(0,843px)] gap-5 items-stretch xl:h-[calc(100vh-128px)] xl:min-h-[520px] xl:max-h-[860px]">

          {/* ============ LEFT PANEL — Text editor ============ */}
          <div className="bg-white rounded-2xl border border-surface-dark/30 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden flex flex-col">
            <div className="px-5 pt-5 pb-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted">Text</p>
            </div>

            {/* Body: global captions list + style controls (Jun 15 —
                was a per-image layer rail; captions are now independent
                of image slides and cycle through evenly over the total
                animation duration). Restyled Jul 24 to match the V5
                CreativeEditor property panel: every control is its own
                stacked, own-row section (label above the control, uniform
                gap) so nothing crams onto a shared flex-wrap line. */}
            <div className="flex-1 min-h-0 flex overflow-hidden">

              {/* Editor panel — full-width, no per-image rail */}
              <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4 flex flex-col gap-3.5">

                {/* Captions list (global) */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-2">
                    Captions
                    {captionsList.length > 0 && (
                      <span className="ml-1 text-dark-muted/70 normal-case tracking-normal">
                        — each shows for ~{Math.round((perImageSec * Math.max(imageUrls.length, 1)) / Math.max(captionsList.length, 1) * 10) / 10}s
                      </span>
                    )}
                  </p>

                  {captionsList.length === 0 ? (
                    /* Empty state: this is the primary CTA — filled solid so
                       it's unmistakable, not a faint dashed outline. */
                    <button
                      type="button"
                      onClick={addCaption}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-lg bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line" />
                      Add text
                    </button>
                  ) : (
                    <div className="space-y-2">
                      {captionsList.map((caption, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2"
                        >
                          {/* Index chip — visual cue that captions cycle
                              in numerical order. */}
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-light text-primary text-[11px] font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <input
                            type="text"
                            value={caption}
                            onChange={(e) => updateCaption(idx, e.target.value)}
                            placeholder={`Text ${idx + 1}…`}
                            maxLength={60}
                            autoFocus={idx === captionsList.length - 1 && caption === ''}
                            className="flex-1 px-3 py-2 rounded-lg bg-white border border-dark-muted/30 text-sm text-dark placeholder:text-dark-muted/50 focus:outline-none focus:border-primary"
                          />
                          <button
                            type="button"
                            onClick={() => removeCaption(idx)}
                            aria-label={`Remove text ${idx + 1}`}
                            className="flex-shrink-0 w-8 h-8 rounded-lg text-dark-muted hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer flex items-center justify-center"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addCaption}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-surface-dark/40 text-xs font-bold text-dark-muted hover:border-primary hover:text-primary transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line" />
                        Add another
                      </button>
                    </div>
                  )}
                </div>

                {/* TEXT STYLE — segmented */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Text style
                  </p>
                  <div className={`grid grid-cols-3 gap-2 ${hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}`}>
                    {TEXT_STYLES.map((s) => {
                      const active = s.id === textStyle;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setTextStyle(s.id)}
                          title={s.description}
                          className={`
                            inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg
                            border-2 transition-all cursor-pointer text-xs font-bold text-center
                            ${active
                              ? 'border-primary bg-primary-light text-primary'
                              : 'border-surface-dark/30 bg-white text-dark-muted hover:border-primary/40'}
                          `}
                        >
                          <i className={`${s.icon} text-sm`} />
                          <span>{s.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* FONT — pills, each rendered in its own family */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Font
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ANIM_FONTS.map((f) => {
                      const active = f.family === captionFont.family;
                      return (
                        <button
                          key={f.family}
                          type="button"
                          onClick={() => setCaptionFont(f)}
                          className={`
                            px-3 py-1.5 rounded-full text-sm border-2 transition-all cursor-pointer
                            ${active
                              ? 'border-primary bg-primary-light text-primary'
                              : 'border-surface-dark/30 bg-white text-dark hover:border-primary/40'}
                          `}
                          style={{ fontFamily: `'${f.family}', sans-serif`, fontWeight: f.weight }}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* SIZE — own row (V5: label carries the value, slider below) */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <label className="text-[11px] text-dark-muted">
                    Size — {Math.round(captionScale * 100)}%
                  </label>
                  <input
                    type="range"
                    min={0.6}
                    max={2}
                    step={0.1}
                    value={captionScale}
                    onChange={(e) => setCaptionScale(Number(e.target.value))}
                    className="w-full accent-primary mt-1"
                  />
                </div>

                {/* PLACEMENT — own row (V5 3×3 grid, label above) */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Placement
                  </p>
                  <div className="grid grid-cols-3 gap-1 w-max">
                    {CAPTION_Y_STOPS.map((y) =>
                      CAPTION_ALIGNS.map((a) => {
                        const active = captionAlign === a && captionY === y;
                        return (
                          <button
                            key={`${a}-${y}`}
                            type="button"
                            onClick={() => { setCaptionAlign(a); setCaptionY(y); }}
                            aria-label={`Place text ${a}, ${y < 0.4 ? 'top' : y > 0.6 ? 'bottom' : 'middle'}`}
                            className={`
                              w-7 h-7 rounded flex items-center justify-center cursor-pointer transition-colors group
                              ${active ? 'bg-primary' : 'bg-surface hover:bg-primary'}
                            `}
                          >
                            <span className={`block w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : 'bg-dark-muted group-hover:bg-white'}`} />
                          </button>
                        );
                      }),
                    )}
                  </div>
                </div>

                {/* TEXT COLOR — own row (V5: label above, swatch + hex) */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Text color
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="relative inline-block w-7 h-7 rounded-md border border-dark-muted/30 overflow-hidden cursor-pointer" style={{ backgroundColor: captionTextColor }}>
                      <input
                        type="color"
                        value={captionTextColor}
                        onChange={(e) => setCaptionTextColor(e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        aria-label="Text color"
                      />
                    </span>
                    <span className="text-xs font-mono text-dark-muted uppercase">
                      {captionTextColor}
                    </span>
                  </div>
                </div>

                {/* BACKDROP — own row (Jul 24). Legibility scrim behind the
                    whole caption block. Each tile shows a mini gradient preview
                    of the darkening direction so the choice reads visually. */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Backdrop
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {SCRIM_OPTS.map((s) => {
                      const active = (captionScrim ?? 'band') === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setCaptionScrim(s.id)}
                          className={`
                            flex items-center gap-2 px-2.5 py-2 rounded-lg border-2 transition-all cursor-pointer text-left
                            ${active
                              ? 'border-primary bg-primary-light'
                              : 'border-surface-dark/30 bg-white hover:border-primary/40'}
                          `}
                        >
                          {/* Gradient preview chip (checkerboard so alpha reads) */}
                          <span
                            className="flex-shrink-0 w-6 h-6 rounded border border-dark-muted/30 overflow-hidden"
                            style={{
                              backgroundColor: '#cbd0dc',
                              backgroundImage: s.grad
                                ? s.grad
                                : 'repeating-linear-gradient(45deg, #e3e7ef 0 4px, #cbd0dc 4px 8px)',
                            }}
                          />
                          <span className={`text-xs font-bold ${active ? 'text-primary' : 'text-dark-muted'}`}>
                            {s.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* FILL — own row (optional bg behind text, Jun 15) */}
                <div className={hasAnyCaption ? '' : 'opacity-40 pointer-events-none'}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted mb-1.5">
                    Fill
                  </p>
                  <div className="flex items-center gap-2">
                    {captionBgColor === null ? (
                      <button
                        type="button"
                        onClick={() => setCaptionBgColor('#000000')}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-dashed border-dark-muted/40 text-xs font-semibold text-dark-muted hover:border-primary hover:text-primary transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line" />
                        Add fill
                      </button>
                    ) : (
                      <>
                        <span className="relative inline-block w-7 h-7 rounded-md border border-dark-muted/30 overflow-hidden cursor-pointer" style={{ backgroundColor: captionBgColor }}>
                          <input
                            type="color"
                            value={captionBgColor}
                            onChange={(e) => setCaptionBgColor(e.target.value)}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            aria-label="Fill color"
                          />
                        </span>
                        <span className="text-xs font-mono text-dark-muted uppercase">
                          {captionBgColor}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCaptionBgColor(null)}
                          aria-label="Remove fill"
                          className="w-6 h-6 rounded-md text-dark-muted hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer flex items-center justify-center"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!hasAnyCaption && (
                  <p className="text-xs text-dark-muted">
                    Click <b>Add text</b> above and type a caption to activate text styling.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ============ RIGHT PANEL — Live preview + PER IMAGE + Generate ============ */}
          <div className="bg-white rounded-2xl border border-surface-dark/30 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden flex flex-col">

            {/* Live animation preview. Bg switched from `bg-dark` to
                `bg-surface-dark/30` (Jun 15 v3) — same fix as Step 1's
                preview frame: dark templates blended into a dark frame. */}
            <div className="flex-1 min-h-0 bg-surface-dark/30 flex items-center justify-center p-4 overflow-hidden">
              <AnimPreview spec={spec} aspect={aspect} loading={specLoading} />
            </div>

            {/* PER IMAGE slider + Generate */}
            <div className="px-5 py-4 border-t border-surface-dark/20 space-y-3">

              {!exportSupported && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Video export needs a recent Chrome, Edge, Safari, or Firefox — this browser doesn't support in-browser video encoding.
                </div>
              )}
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700 flex items-start justify-between gap-2">
                  <span>{error}</span>
                  {error.includes('credits') && (
                    <button
                      onClick={() => navigate('/checkout')}
                      className="flex-shrink-0 text-primary font-bold hover:underline cursor-pointer"
                    >
                      Upgrade →
                    </button>
                  )}
                </div>
              )}
              {exporting && (
                <div>
                  <div className="h-2 rounded-full bg-surface overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-200"
                      style={{ width: `${Math.round(exportProgress * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-dark-muted mt-1.5">
                    Encoding your MP4 — {Math.round(exportProgress * 100)}%
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.12em] text-dark-muted">
                  Per image
                  <input
                    type="range"
                    min={0.5}
                    max={5}
                    step={0.25}
                    value={perImageSec}
                    onChange={(e) => setPerImageSec(Number(e.target.value))}
                    className="w-32 accent-primary"
                  />
                  <span className="text-dark normal-case tabular-nums">{perImageSec.toFixed(2)}s</span>
                </label>
                {spec && (
                  <span className="text-xs text-dark-muted">
                    Total: {(spec.images.length * perImageSec).toFixed(1)}s
                  </span>
                )}
              </div>

              {/* Jun 22 — single Generate CTA. Does encode → save to My
                  Creatives → download MP4 in one click. Previously two
                  separate buttons each doing half the action, which
                  surprised users who hit "Generate Animation" expecting
                  a complete result. Credit's charged once per session
                  (chargedRef), so regenerating is free after the first. */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => runExport()}
                  disabled={!canExport}
                  className={`
                    flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer shadow-md
                    ${canExport
                      ? 'bg-primary hover:bg-primary-hover text-white shadow-primary/25'
                      : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed shadow-none'}
                  `}
                >
                  <i className={exporting ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'} />
                  {exporting
                    ? `Generating… ${Math.round(exportProgress * 100)}%`
                    : savedPostId
                      ? 'Generate again'
                      : 'Generate Animation'}
                </button>
              </div>
              {/* Post-generation indicator — confirms save + download both
                  happened. Subtle so it doesn't compete with the CTA. */}
              {savedPostId && !exporting && (
                <p className="text-[11px] text-emerald-700 text-center flex items-center justify-center gap-1.5">
                  <i className="ri-check-line text-sm" />
                  Saved to My Creatives + MP4 downloaded
                </p>
              )}

              <p className="text-[11px] text-dark-muted text-center">
                {exportFree
                  ? 'Already charged this session — further exports are free.'
                  : balance !== null && balance < 1
                    ? 'Exporting costs 1 credit — you have 0.'
                    : 'Exporting costs 1 credit (charged once per session).'}
              </p>
            </div>
          </div>

        </div>
      </div>
    </AppLayoutV2>
  );
}

// ============================================================================
// TemplatesRow — horizontal-scrolling template row with chevron buttons
// when the row overflows the visible width. Each thumb is a fixed 88×120
// (4:5-ish) tile showing the template's preview MP4 looping inline.
// ============================================================================

function TemplatesRow({
  templates,
  activeId,
  onPick,
}: {
  templates: readonly AnimationTemplate[];
  activeId: string;
  onPick: (t: AnimationTemplate) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollL, setCanScrollL] = useState(false);
  const [canScrollR, setCanScrollR] = useState(false);

  // Track scroll position to show/hide chevrons.
  const updateChevrons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollL(el.scrollLeft > 8);
    setCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateChevrons();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateChevrons, { passive: true });
    window.addEventListener('resize', updateChevrons);
    return () => {
      el.removeEventListener('scroll', updateChevrons);
      window.removeEventListener('resize', updateChevrons);
    };
  }, [updateChevrons]);

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* Left chevron */}
      {canScrollL && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll templates left"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-surface-dark/40 shadow-md flex items-center justify-center text-dark-muted hover:text-primary transition-colors"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
      )}
      {/* Right chevron */}
      {canScrollR && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Scroll templates right"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-surface-dark/40 shadow-md flex items-center justify-center text-dark-muted hover:text-primary transition-colors"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide pb-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {templates.map((t) => {
          const active = t.id === activeId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              title={t.name}
              className={`
                flex-shrink-0 w-[88px] rounded-lg overflow-hidden bg-surface
                border-2 transition-all cursor-pointer text-left
                ${active
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-transparent hover:border-primary/40'}
              `}
            >
              <div className="aspect-[4/5] bg-dark relative">
                {t.previewVideo ? (
                  <video
                    src={t.previewVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40">
                    <i className="ri-movie-line text-lg" />
                  </div>
                )}
              </div>
              <p className={`px-2 py-1 text-[10px] font-bold truncate ${active ? 'text-primary' : 'text-dark'}`}>
                {t.name}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// TemplatePreviewVideo — big preview area on Step 1. Plays the template's
// baked MP4 (loops). Aspect-locked to the user's selected output aspect
// so they see the canvas shape they'll get.
// ============================================================================

function TemplatePreviewVideo({
  template,
  aspect,
}: {
  template: AnimationTemplate;
  aspect: AnimAspect;
}) {
  const aspectClass =
    aspect === '9:16' ? 'aspect-[9/16] max-w-[260px]'
      : aspect === '4:5' ? 'aspect-[4/5] max-w-[320px]'
      : 'aspect-square max-w-[400px]';
  return (
    <div className={`relative w-full ${aspectClass} bg-black rounded-xl overflow-hidden ring-1 ring-surface-dark/60 shadow-md`}>
      {template.previewVideo ? (
        <video
          key={template.id}
          src={template.previewVideo}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/40">
          <i className="ri-movie-line text-4xl" />
          <span className="text-xs font-semibold">No preview for this template</span>
        </div>
      )}
      <p className="absolute bottom-2 left-2 right-2 text-[10px] text-white/80 font-semibold bg-black/40 backdrop-blur px-2 py-1 rounded">
        {template.name}
      </p>
    </div>
  );
}

// ============================================================================
// AnimPreview — Step 2 live preview with transport (play/pause + scrub bar).
// Draws at HALF the export resolution so the rAF loop stays cheap; CSS
// scales the canvas into the aspect box. Playhead lives in a ref and the
// slider updates via direct DOM writes inside the rAF tick — zero React
// re-renders at 60fps. Scrubbing writes the ref; drawAnimationFrame is
// pure in t so seeking is just "draw at that t". Playhead SURVIVES spec
// identity changes (caption keystrokes, style swaps) since the ref
// persists across effect re-subscriptions.
// ============================================================================

function AnimPreview({ spec, aspect, loading }: {
  spec: AnimationSpec | null;
  aspect: AnimAspect;
  loading: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);
  const timeLabelRef = useRef<HTMLSpanElement>(null);
  const [playing, setPlaying] = useState(true);
  const tRef = useRef(0);

  const totalMs = spec ? specDurationMs(spec) : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !spec) return;
    const { w, h } = ANIM_DIMENSIONS[aspect];
    const pw = Math.round(w / 2);
    const ph = Math.round(h / 2);
    canvas.width = pw;
    canvas.height = ph;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const total = specDurationMs(spec);
    if (tRef.current >= total) tRef.current = 0;
    let raf = 0;
    let last: number | null = null;
    const tick = (now: number) => {
      if (playing && last != null) {
        tRef.current = (tRef.current + (now - last)) % total;
      }
      last = now;
      drawAnimationFrame(ctx, spec, tRef.current, pw, ph);
      if (sliderRef.current) sliderRef.current.value = String(Math.round(tRef.current));
      if (timeLabelRef.current) {
        timeLabelRef.current.textContent =
          `${(tRef.current / 1000).toFixed(1)}s / ${(total / 1000).toFixed(1)}s`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [spec, aspect, playing]);

  // Height-driven aspect: the box fills the AVAILABLE height (aspect kept,
  // width derives) so the image never grows past the panel and the transport
  // row below always stays visible (was width-driven → tall 9:16 pushed the
  // scrub bar out of the overflow-hidden panel).
  const aspectBoxClass =
    aspect === '9:16' ? 'aspect-[9/16]'
      : aspect === '4:5' ? 'aspect-[4/5]'
      : 'aspect-square';
  const transportWidth =
    aspect === '9:16' ? 'max-w-[360px]'
      : aspect === '4:5' ? 'max-w-[440px]'
      : 'max-w-[520px]';

  return (
    <div className="w-full h-full min-h-0 flex flex-col items-center gap-2">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center">
        <div className={`relative h-full max-w-full ${aspectBoxClass} bg-black rounded-xl overflow-hidden ring-1 ring-surface-dark/60 shadow-md`}>
          {spec ? (
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/40">
              {loading ? (
                <i className="ri-loader-4-line text-3xl animate-spin" />
              ) : (
                <>
                  <i className="ri-movie-line text-4xl" />
                  <span className="text-xs font-semibold">Your animation previews here</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {spec && (
        // Transport sits BELOW the image on the light panel bg → dark-on-light
        // colors (was white-on-dark, invisible here). flex-shrink-0 → never clipped.
        <div className={`flex-shrink-0 w-full ${transportWidth} mx-auto flex items-center gap-2.5 px-1`}>
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            aria-label={playing ? 'Pause' : 'Play'}
            className="w-8 h-8 rounded-full bg-dark/10 hover:bg-dark/20 text-dark flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors"
          >
            <i className={playing ? 'ri-pause-fill' : 'ri-play-fill'} />
          </button>
          <input
            ref={sliderRef}
            type="range"
            min={0}
            max={Math.max(1, totalMs)}
            step={33}
            defaultValue={0}
            onInput={(e) => {
              tRef.current = Number((e.target as HTMLInputElement).value);
            }}
            aria-label="Scrub timeline"
            className="flex-1 accent-primary cursor-pointer"
          />
          <span
            ref={timeLabelRef}
            className="text-[11px] font-bold text-dark-muted tabular-nums flex-shrink-0 min-w-[76px] text-right"
          >
            0.0s / {(totalMs / 1000).toFixed(1)}s
          </span>
        </div>
      )}
    </div>
  );
}
